import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Notice } from './ui';
import { Dialog } from './Dialog';
import { api, ApiError } from '../lib/api';
import { useSchool } from '../lib/school';
import { useCodeJoins, usePeople } from '../lib/people';
import { useMe, useSession } from '../lib/session';

/**
 * The organisation code, on People: copy it, and (owners) switch code joining
 * off or make a new code. Anyone joining with it waits for a leader's approval.
 */
export function OrgCodePanel() {
  const me = useMe();
  const { refresh } = useSession();
  const qc = useQueryClient();
  const school = useSchool();
  const people = usePeople();
  const codeMode = useCodeJoins();
  const isOwner = Boolean(me.is_owner) || me.role === 'system_admin';
  const codeJoins = people.data?.codeJoins ?? 'approval';
  const code = school.data?.invite_code ?? null;
  const [copied, setCopied] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      try {
        localStorage.setItem(`educaitor.codeShared.${me.school_id ?? ''}`, '1');
      } catch {
        // Only affects the Overview checklist tick.
      }
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard refused: the code is on screen to copy by hand.
    }
  };

  const rotate = async () => {
    setRotating(true);
    try {
      await api('/schools/mine/invite-code/rotate', { method: 'POST', body: '{}' });
      await Promise.all([qc.invalidateQueries({ queryKey: ['school'] }), refresh()]);
      setMessage({ tone: 'success', text: 'New organisation code made. The old one no longer works.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setRotating(false);
      setRotateOpen(false);
    }
  };

  return (
    <section className="panel panel-tint form-section org-code">
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      <div className="code-row">
        <div>
          <h2>Organisation code</h2>
          <p className="muted">
            {codeJoins === 'off'
              ? 'Switched off. People can only join by email invite.'
              : 'Staff can enter this in the app to ask to join. A leader approves each one. Email invites don’t need it.'}
          </p>
        </div>
        {codeJoins === 'off' || !code ? null : (
          <div className="code-row">
            <span className="code">{code}</span>
            <Button variant="secondary" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </div>
      {isOwner ? (
        <div className="form-actions">
          <label className="agree">
            <input
              type="checkbox"
              checked={codeJoins !== 'off'}
              disabled={codeMode.isPending}
              onChange={(e) => codeMode.mutate(e.target.checked ? 'approval' : 'off')}
            />
            <span>Let people ask to join with the code</span>
          </label>
          {codeJoins === 'off' ? null : (
            <Button variant="ghost" onClick={() => setRotateOpen(true)}>
              Make a new code
            </Button>
          )}
        </div>
      ) : null}

      <Dialog open={rotateOpen} onClose={() => setRotateOpen(false)} title="Make a new organisation code?">
        <p className="muted">
          The current code stops working straight away. People already in {school.data?.name ?? 'your organisation'} aren&rsquo;t
          affected, but anyone you&rsquo;ve given the old code to will need the new one. Email invites keep working.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setRotateOpen(false)}>
            Keep current code
          </Button>
          <Button onClick={rotate} loading={rotating}>
            Make new code
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
